import React, { useState, useMemo, useRef } from 'react';
import Swal from 'sweetalert2';
import {
  PREDEFINED_INVENTORY_ITEMS,
  PredefinedInventoryItem
} from '../../constants/predefinedInventoryItems';
import {
  StoreInventoryItem,
  createBatchStoreInventoryItems
} from '../../services/supabaseService';
import { useNotification } from '../member/NotificationProvider';

interface PredefinedInventoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  storeId: number;
  existingItems: StoreInventoryItem[];
  onItemsAdded: (newItems: StoreInventoryItem[]) => void;
}

interface SelectedItemConfig {
  quantity: number;
  cost: number | null;
  minimum_quantity: number | null;
}

export const PredefinedInventoryModal: React.FC<PredefinedInventoryModalProps> = ({
  isOpen,
  onClose,
  storeId,
  existingItems,
  onItemsAdded
}) => {
  const { showNotification } = useNotification();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // States
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [search, setSearch] = useState<string>('');
  const [onlyNonAdded, setOnlyNonAdded] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Custom imported items state (in addition to PREDEFINED_INVENTORY_ITEMS)
  const [customItems, setCustomItems] = useState<PredefinedInventoryItem[]>([]);

  // Selection Map: itemId -> Config
  const [selectedMap, setSelectedMap] = useState<Record<string, SelectedItemConfig>>({});

  // Merge predefined + custom uploaded items
  const allAvailableItems = useMemo(() => {
    return [...customItems, ...PREDEFINED_INVENTORY_ITEMS];
  }, [customItems]);

  // Set of names already in store (case-insensitive)
  const existingNamesMap = useMemo(() => {
    const map = new Map<string, StoreInventoryItem>();
    existingItems.forEach(item => {
      map.set(item.name.toLowerCase().trim(), item);
    });
    return map;
  }, [existingItems]);

  // Available categories
  const categories = useMemo(() => {
    const set = new Set<string>();
    allAvailableItems.forEach(item => {
      if (item.category) set.add(item.category);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [allAvailableItems]);

  // Filtered Items
  const filteredItems = useMemo(() => {
    return allAvailableItems.filter(item => {
      // Category filter
      if (selectedCategory !== 'all' && item.category !== selectedCategory) {
        return false;
      }

      // Already added filter
      const isAlreadyAdded = existingNamesMap.has(item.name.toLowerCase().trim());
      if (onlyNonAdded && isAlreadyAdded) {
        return false;
      }

      // Search filter
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        const matchesName = item.name.toLowerCase().includes(q);
        const matchesDesc = (item.description || '').toLowerCase().includes(q);
        const matchesCat = (item.category || '').toLowerCase().includes(q);
        if (!matchesName && !matchesDesc && !matchesCat) return false;
      }

      return true;
    });
  }, [allAvailableItems, selectedCategory, onlyNonAdded, search, existingNamesMap]);

  // Toggle selection of a single item
  const handleToggleItem = (item: PredefinedInventoryItem) => {
    const isAlreadyAdded = existingNamesMap.has(item.name.toLowerCase().trim());
    if (isAlreadyAdded) return; // Prevent selecting items already in the store

    setSelectedMap(prev => {
      const next = { ...prev };
      if (next[item.id]) {
        delete next[item.id];
      } else {
        next[item.id] = {
          quantity: 0,
          cost: item.suggested_cost !== undefined ? item.suggested_cost : null,
          minimum_quantity: item.suggested_minimum_quantity || null
        };
      }
      return next;
    });
  };

  // Update item config
  const handleUpdateConfig = (
    itemId: string,
    field: keyof SelectedItemConfig,
    value: number | null
  ) => {
    setSelectedMap(prev => {
      const current = prev[itemId] || { quantity: 0, cost: null, minimum_quantity: null };
      return {
        ...prev,
        [itemId]: {
          ...current,
          [field]: value
        }
      };
    });
  };

  // Quick Action: Select Essential Store Kit
  const handleSelectEssentialKit = () => {
    const newSelected: Record<string, SelectedItemConfig> = {};
    let count = 0;

    allAvailableItems.forEach(item => {
      const isAlreadyAdded = existingNamesMap.has(item.name.toLowerCase().trim());
      if (item.isEssential && !isAlreadyAdded) {
        newSelected[item.id] = {
          quantity: item.suggested_minimum_quantity || 0,
          cost: item.suggested_cost !== undefined ? item.suggested_cost : null,
          minimum_quantity: item.suggested_minimum_quantity || null
        };
        count++;
      }
    });

    setSelectedMap(newSelected);
    showNotification(
      count > 0
        ? `Kit Essencial selecionado (${count} novos itens)!`
        : 'Todos os itens essenciais já estão cadastrados na sua loja.',
      count > 0 ? 'success' : 'info'
    );
  };

  // Select all visible items
  const handleSelectAllVisible = () => {
    const newSelected = { ...selectedMap };
    let addedCount = 0;

    filteredItems.forEach(item => {
      const isAlreadyAdded = existingNamesMap.has(item.name.toLowerCase().trim());
      if (!isAlreadyAdded) {
        if (!newSelected[item.id]) {
          newSelected[item.id] = {
            quantity: 0,
            cost: item.suggested_cost !== undefined ? item.suggested_cost : null,
            minimum_quantity: item.suggested_minimum_quantity || null
          };
          addedCount++;
        }
      }
    });

    setSelectedMap(newSelected);
    showNotification(`${addedCount} itens selecionados.`, 'info');
  };

  // Deselect all
  const handleDeselectAll = () => {
    setSelectedMap({});
  };

  // Export predefined items as JSON file
  const handleDownloadTemplate = () => {
    const exportData = allAvailableItems.map(item => ({
      name: item.name,
      category: item.category,
      unit: item.unit,
      minimum_quantity: item.suggested_minimum_quantity,
      cost: item.suggested_cost || null,
      description: item.description
    }));

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'predefined_inventory_items.json';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showNotification('Arquivo JSON baixado com sucesso!', 'success');
  };

  // Import external JSON file (e.g. from user or friend)
  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = event => {
      try {
        const content = event.target?.result as string;
        const parsed = JSON.parse(content);

        let itemsArray: any[] = [];
        if (Array.isArray(parsed)) {
          itemsArray = parsed;
        } else if (parsed && Array.isArray(parsed.items)) {
          itemsArray = parsed.items;
        } else if (parsed && Array.isArray(parsed.data)) {
          itemsArray = parsed.data;
        } else {
          throw new Error('O arquivo não contém uma lista válida de itens.');
        }

        const validImported: PredefinedInventoryItem[] = itemsArray
          .filter(i => i && typeof i.name === 'string' && i.name.trim().length > 0)
          .map((i, idx) => ({
            id: `imported-${Date.now()}-${idx}`,
            name: i.name.trim(),
            category: i.category?.trim() || 'Outros',
            unit: i.unit?.trim() || 'un',
            suggested_minimum_quantity: Number(i.minimum_quantity) || Number(i.min_qty) || 0,
            suggested_cost: i.cost !== undefined && i.cost !== null ? Number(i.cost) : undefined,
            description: i.description?.trim() || 'Item importado via arquivo predefinido',
            isEssential: false
          }));

        if (validImported.length === 0) {
          showNotification('Nenhum item válido encontrado no arquivo.', 'error');
          return;
        }

        // Add to customItems
        setCustomItems(prev => [...validImported, ...prev]);

        // Auto-select valid non-duplicate items
        const autoSelected: Record<string, SelectedItemConfig> = {};
        validImported.forEach(item => {
          if (!existingNamesMap.has(item.name.toLowerCase().trim())) {
            autoSelected[item.id] = {
              quantity: 0,
              cost: item.suggested_cost !== undefined ? item.suggested_cost : null,
              minimum_quantity: item.suggested_minimum_quantity || null
            };
          }
        });
        setSelectedMap(prev => ({ ...prev, ...autoSelected }));

        Swal.fire({
          icon: 'success',
          title: 'Arquivo importado!',
          text: `${validImported.length} itens foram carregados com sucesso para visualização e seleção.`
        });
      } catch (err: any) {
        console.error('Error importing JSON inventory:', err);
        showNotification(
          err.message || 'Erro ao processar o arquivo JSON.',
          'error'
        );
      } finally {
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    };

    reader.readAsText(file);
  };

  // Submit and save selected items to store inventory
  const handleSubmitBatch = async () => {
    const selectedIds = Object.keys(selectedMap);
    if (selectedIds.length === 0) {
      showNotification('Selecione pelo menos um item para adicionar.', 'info');
      return;
    }

    const itemsToCreate = selectedIds
      .map(id => {
        const itemDef = allAvailableItems.find(i => i.id === id);
        if (!itemDef) return null;
        const config = selectedMap[id];
        return {
          name: itemDef.name,
          category: itemDef.category,
          unit: itemDef.unit,
          description: itemDef.description,
          quantity: config.quantity,
          minimum_quantity: config.minimum_quantity,
          cost: config.cost,
          active: true,
          initial_reason: 'Cadastro via catálogo de itens pré-definidos'
        };
      })
      .filter(Boolean) as Array<{
      name: string;
      category: string;
      unit: string;
      description: string;
      quantity: number;
      minimum_quantity: number | null;
      cost: number | null;
      active: boolean;
      initial_reason: string;
    }>;

    const confirm = await Swal.fire({
      title: 'Adicionar ao Almoxarifado?',
      text: `Deseja adicionar ${itemsToCreate.length} ${
        itemsToCreate.length === 1 ? 'item' : 'itens'
      } ao inventário da loja?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sim, adicionar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#9333ea',
      cancelButtonColor: '#334155',
      background: '#0f172a',
      color: '#fff'
    });

    if (!confirm.isConfirmed) return;

    try {
      setIsSubmitting(true);
      const result = await createBatchStoreInventoryItems(storeId, itemsToCreate);

      if (result.created.length > 0) {
        onItemsAdded(result.created);
      }

      let msg = `${result.created.length} ${
        result.created.length === 1 ? 'item adicionado' : 'itens adicionados'
      } com sucesso!`;

      if (result.skipped.length > 0) {
        msg += ` (${result.skipped.length} já existiam e foram ignorados)`;
      }

      await Swal.fire({
        icon: 'success',
        title: 'Inventário Atualizado!',
        text: msg,
        background: '#0f172a',
        color: '#fff',
        confirmButtonColor: '#9333ea'
      });

      setSelectedMap({});
      onClose();
    } catch (err: any) {
      console.error('Error batch creating inventory items:', err);
      showNotification(
        err.message || 'Erro ao adicionar os itens ao inventário.',
        'error'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const totalSelectedCount = Object.keys(selectedMap).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-5xl max-h-[92vh] flex flex-col shadow-2xl overflow-hidden">
        {/* Modal Header */}
        <div className="p-4 sm:p-6 border-b border-slate-800 flex items-center justify-between bg-slate-950/40 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-tr from-purple-600 to-indigo-600 flex items-center justify-center text-white shadow-lg shadow-purple-600/30">
              <i className="fas fa-boxes-packing text-lg sm:text-xl"></i>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg sm:text-xl font-black text-white uppercase tracking-tight">
                  Itens Pré-definidos para Inventário
                </h3>
                <span className="bg-purple-500/20 text-purple-300 border border-purple-500/30 text-[10px] font-black px-2 py-0.5 rounded-full uppercase">
                  50+ Modelos
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-0.5">
                Selecione insumos comuns de card games, lanchonete, limpeza e embalagens para cadastrar de forma rápida.
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white p-2 rounded-xl hover:bg-slate-800 transition-colors"
          >
            <i className="fas fa-xmark text-lg"></i>
          </button>
        </div>

        {/* Toolbar: Kit shortcuts, Import & Export */}
        <div className="px-4 sm:px-6 py-3 bg-slate-950/60 border-b border-slate-800 flex flex-wrap items-center justify-between gap-2.5 shrink-0">
          <div className="flex items-center flex-wrap gap-2">
            <button
              type="button"
              onClick={handleSelectEssentialKit}
              className="bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border border-amber-500/30 px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 active:scale-95"
            >
              <i className="fas fa-star text-[11px] text-amber-400"></i>
              <span>Kit Básico para Lojas (18 essenciais)</span>
            </button>

            <button
              type="button"
              onClick={handleSelectAllVisible}
              className="bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white px-3 py-1.5 rounded-xl text-xs font-medium transition-all"
            >
              Selecionar Visíveis
            </button>

            {totalSelectedCount > 0 && (
              <button
                type="button"
                onClick={handleDeselectAll}
                className="bg-slate-800/60 hover:bg-slate-800 text-rose-300 hover:text-rose-200 px-3 py-1.5 rounded-xl text-xs font-medium transition-all"
              >
                Limpar Seleção ({totalSelectedCount})
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Download JSON template */}
            <button
              type="button"
              onClick={handleDownloadTemplate}
              className="bg-slate-800/80 hover:bg-slate-800 text-slate-300 hover:text-white px-3 py-1.5 rounded-xl text-xs font-medium border border-slate-700/60 transition-all flex items-center gap-1.5"
              title="Baixar modelo predefinido em formato JSON"
            >
              <i className="fas fa-download text-xs text-purple-400"></i>
              <span className="hidden sm:inline">Baixar JSON</span>
            </button>

            {/* Upload Custom JSON */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImportFile}
              accept=".json"
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="bg-purple-900/40 hover:bg-purple-900/60 text-purple-200 border border-purple-700/60 px-3 py-1.5 rounded-xl text-xs font-medium transition-all flex items-center gap-1.5"
              title="Importar um arquivo JSON com novos itens"
            >
              <i className="fas fa-file-import text-xs text-purple-400"></i>
              <span>Importar Arquivo</span>
            </button>
          </div>
        </div>

        {/* Filters bar: Search & Category tabs */}
        <div className="p-4 sm:px-6 sm:py-3.5 bg-slate-900/80 border-b border-slate-800 flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between shrink-0">
          {/* Search box */}
          <div className="relative flex-1 max-w-md">
            <i className="fas fa-magnifying-glass absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs"></i>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar insumos (ex: álcool, envelope, café, toploader)..."
              className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-9 pr-8 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 transition-colors"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white text-xs"
              >
                <i className="fas fa-xmark"></i>
              </button>
            )}
          </div>

          {/* Toggle only non-added */}
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer select-none text-xs text-slate-300">
              <input
                type="checkbox"
                checked={onlyNonAdded}
                onChange={e => setOnlyNonAdded(e.target.checked)}
                className="rounded border-slate-700 text-purple-600 focus:ring-purple-500 bg-slate-950"
              />
              <span>Ocultar já adicionados</span>
            </label>
          </div>
        </div>

        {/* Categories Tabs */}
        <div className="px-4 sm:px-6 py-2 bg-slate-950/40 border-b border-slate-800/80 flex items-center gap-1.5 overflow-x-auto custom-scrollbar shrink-0">
          <button
            type="button"
            onClick={() => setSelectedCategory('all')}
            className={`px-3 py-1 rounded-lg text-xs font-bold uppercase tracking-wider shrink-0 transition-colors ${
              selectedCategory === 'all'
                ? 'bg-purple-600 text-white'
                : 'text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            Todos ({allAvailableItems.length})
          </button>
          {categories.map(cat => {
            const count = allAvailableItems.filter(i => i.category === cat).length;
            const isCurrent = selectedCategory === cat;
            return (
              <button
                key={cat}
                type="button"
                onClick={() => setSelectedCategory(cat)}
                className={`px-3 py-1 rounded-lg text-xs font-bold shrink-0 transition-colors flex items-center gap-1.5 ${
                  isCurrent
                    ? 'bg-purple-600 text-white'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
              >
                <span>{cat}</span>
                <span className="text-[10px] opacity-70">({count})</span>
              </button>
            );
          })}
        </div>

        {/* Items List (Scrollable) */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-3 custom-scrollbar">
          {filteredItems.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <i className="fas fa-boxes-packing text-4xl mb-3 opacity-30"></i>
              <p className="text-sm font-medium">Nenhum item encontrado com os filtros atuais.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {filteredItems.map(item => {
                const isSelected = !!selectedMap[item.id];
                const isAlreadyInStore = existingNamesMap.has(item.name.toLowerCase().trim());
                const existingItem = existingNamesMap.get(item.name.toLowerCase().trim());
                const config = selectedMap[item.id] || {
                  quantity: 0,
                  cost: item.suggested_cost !== undefined ? item.suggested_cost : null,
                  minimum_quantity: item.suggested_minimum_quantity || null
                };

                return (
                  <div
                    key={item.id}
                    className={`rounded-2xl border p-4 transition-all relative flex flex-col justify-between ${
                      isAlreadyInStore
                        ? 'bg-slate-950/40 border-slate-800/60 opacity-60'
                        : isSelected
                        ? 'bg-purple-950/20 border-purple-500/60 shadow-lg shadow-purple-950/30 ring-1 ring-purple-500/30'
                        : 'bg-slate-950/60 border-slate-800 hover:border-slate-700 hover:bg-slate-950'
                    }`}
                  >
                    <div>
                      {/* Top bar: Category + status badge */}
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-slate-800 text-slate-300 border border-slate-700/50">
                            {item.category}
                          </span>
                          {item.isEssential && (
                            <span className="text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-amber-500/10 text-amber-300 border border-amber-500/30 flex items-center gap-1">
                              <i className="fas fa-star text-[9px] text-amber-400"></i> Essencial
                            </span>
                          )}
                          <span className="text-[10px] text-slate-500 font-mono">
                            Unid: {item.unit}
                          </span>
                        </div>

                        {/* Already in store badge OR Checkbox */}
                        {isAlreadyInStore ? (
                          <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 flex items-center gap-1">
                            <i className="fas fa-check text-[9px]"></i>
                            <span>No Almoxarifado ({existingItem?.quantity} {existingItem?.unit})</span>
                          </span>
                        ) : (
                          <label className="flex items-center gap-1.5 cursor-pointer shrink-0">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleItem(item)}
                              className="w-4 h-4 rounded border-slate-700 text-purple-600 focus:ring-purple-500 bg-slate-900 cursor-pointer"
                            />
                            <span className="text-xs font-bold text-slate-300 select-none">
                              {isSelected ? 'Selecionado' : 'Selecionar'}
                            </span>
                          </label>
                        )}
                      </div>

                      {/* Name & description */}
                      <h4 className="text-sm font-bold text-white leading-snug">
                        {item.name}
                      </h4>
                      <p className="text-xs text-slate-400 mt-1 line-clamp-2">
                        {item.description}
                      </p>
                    </div>

                    {/* Interactive config row if selected */}
                    {isSelected && !isAlreadyInStore && (
                      <div className="mt-3 pt-3 border-t border-purple-500/20 bg-purple-950/30 -mx-4 -mb-4 p-3 rounded-b-2xl grid grid-cols-3 gap-2">
                        {/* Qtd Inicial */}
                        <div>
                          <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                            Qtd Inicial
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="any"
                            value={config.quantity}
                            onChange={e =>
                              handleUpdateConfig(
                                item.id,
                                'quantity',
                                Math.max(0, parseFloat(e.target.value) || 0)
                              )
                            }
                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-white focus:outline-none focus:border-purple-500"
                          />
                        </div>

                        {/* Estoque Mínimo */}
                        <div>
                          <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                            Est. Mínimo
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="any"
                            value={config.minimum_quantity ?? ''}
                            placeholder="Ex: 5"
                            onChange={e =>
                              handleUpdateConfig(
                                item.id,
                                'minimum_quantity',
                                e.target.value === '' ? null : Math.max(0, parseFloat(e.target.value) || 0)
                              )
                            }
                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-white focus:outline-none focus:border-purple-500"
                          />
                        </div>

                        {/* Custo Unitário (R$) */}
                        <div>
                          <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                            Custo (R$)
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={config.cost ?? ''}
                            placeholder="0,00"
                            onChange={e =>
                              handleUpdateConfig(
                                item.id,
                                'cost',
                                e.target.value === '' ? null : Math.max(0, parseFloat(e.target.value) || 0)
                              )
                            }
                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-white focus:outline-none focus:border-purple-500"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-4 sm:p-5 bg-slate-950/80 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-3 shrink-0">
          <div className="text-xs text-slate-400">
            {totalSelectedCount > 0 ? (
              <span className="text-purple-300 font-bold">
                {totalSelectedCount} {totalSelectedCount === 1 ? 'item selecionado' : 'itens selecionados'} para adicionar ao estoque interno.
              </span>
            ) : (
              <span>Selecione itens na lista acima ou use o Kit Essencial para começar.</span>
            )}
          </div>

          <div className="flex items-center gap-2.5 w-full sm:w-auto">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 sm:flex-none px-4 py-2.5 rounded-xl border border-slate-700 hover:bg-slate-800 text-slate-300 font-bold text-xs uppercase tracking-wider transition-colors"
            >
              Cancelar
            </button>

            <button
              type="button"
              onClick={handleSubmitBatch}
              disabled={totalSelectedCount === 0 || isSubmitting}
              className="flex-1 sm:flex-none bg-purple-600 hover:bg-purple-500 disabled:opacity-50 disabled:pointer-events-none text-white px-5 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest transition-all shadow-lg shadow-purple-600/30 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <i className="fas fa-spinner fa-spin text-xs"></i>
                  <span>Adicionando...</span>
                </>
              ) : (
                <>
                  <i className="fas fa-plus text-xs"></i>
                  <span>Adicionar {totalSelectedCount > 0 ? `(${totalSelectedCount})` : ''} ao Almoxarifado</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
